/* eslint-disable jsx-a11y/anchor-is-valid */
import { Button, Card, Checkbox, Label, TextInput } from "flowbite-react";
import { useState } from "react";
import type { FC, FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { handleAuthError } from "../../lib/supabase";
import type { AuthError } from "@supabase/supabase-js";

const SignInPage: FC = function () {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Log the attempt
    console.log("Attempting sign in with email:", email);

    // Check if form is valid
    if (!email || !password) {
      setError("Bitte geben Sie E-Mail und Passwort ein");
      setLoading(false);
      return;
    }

    try {
      // Attempt sign in
      console.log("Starting sign in process...");
      const { error: signInError, user } = await signIn(email, password);
      console.log("Sign in response received:", {
        error: signInError,
        hasUser: !!user,
      });

      if (signInError) {
        console.error("Sign in error:", signInError);
        setError(handleAuthError(signInError as AuthError));
        setLoading(false);
        return;
      }

      if (!user) {
        console.error("No user returned after successful sign in");
        setError("Benutzerinformationen konnten nicht abgerufen werden");
        setLoading(false);
        return;
      }

      // Get the redirect path from location state or default to home
      const from =
        (location.state as { from?: { pathname: string } }).from?.pathname ||
        "/";
      console.log("Sign in successful, redirecting to:", from);

      // Navigate to the redirect path
      navigate(from, { replace: true });
    } catch (err) {
      console.error("Unexpected error during sign in:", err);
      setError(
        "Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center px-6 lg:h-screen lg:gap-y-12">
      <div className="my-6 flex items-center gap-x-1 lg:my-0">
        <img alt="D2D Boom" src="/images/logo.webp" className="mr-3 h-12" />
        <span className="self-center whitespace-nowrap text-2xl font-semibold dark:text-white">
          D2D Boom
        </span>
      </div>
      <Card className="w-full p-6 md:max-w-screen-sm">
        <h1 className="mb-4 text-2xl font-bold dark:text-white md:text-3xl">
          In Plattform anmelden
        </h1>
        <form onSubmit={handleSubmit}>
          <div className="mb-4 flex flex-col gap-y-3">
            <Label htmlFor="email">Ihre E-Mail</Label>
            <TextInput
              id="email"
              name="email"
              placeholder="name@company.com"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="mb-6 flex flex-col gap-y-3">
            <Label htmlFor="password">Ihr Passwort</Label>
            <TextInput
              id="password"
              name="password"
              placeholder="••••••••"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-x-3">
              <Checkbox id="rememberMe" name="rememberMe" />
              <Label htmlFor="rememberMe">Angemeldet bleiben</Label>
            </div>
            <button
              type="button"
              onClick={() => navigate("/authentication/reset-password")}
              className="w-1/2 text-right text-sm text-primary-600 dark:text-primary-300"
            >
              Passwort vergessen?
            </button>
          </div>
          {error && <div className="mb-4 text-sm text-red-600">{error}</div>}
          <div className="mb-6">
            <Button
              type="submit"
              className="w-full lg:w-auto"
              disabled={loading}
            >
              <div className="flex items-center gap-2">
                {loading && (
                  <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                )}
                {loading ? "Anmeldung läuft..." : "Anmelden"}
              </div>
            </Button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-300">
            Noch nicht registriert?&nbsp;
            <button
              type="button"
              onClick={() => navigate("/authentication/sign-up")}
              className="text-primary-600 dark:text-primary-300"
            >
              Konto erstellen
            </button>
          </p>
        </form>
      </Card>
    </div>
  );
};

export default SignInPage;
